import Phaser from "phaser";
import { StageConfig } from "../data/StageConfig";
import { Block } from "../entities/Block";
import { Tower } from "../entities/Tower";
import { clamp } from "../utils/Clamp";

export interface SwipeResult {
  block: Block;
  deltaX: number;
  speed: number; // px/ms
}

export class InputSystem {
  private scene: Phaser.Scene;
  private tower: Tower;
  private touchStartX: number = 0;
  private touchStartY: number = 0;
  private touchStartTime: number = 0;
  private targetBlock: Block | null = null;
  private isActive: boolean = false;
  private onSwipe: ((result: SwipeResult) => void) | null = null;

  // Visual feedback
  private swipeIndicator: Phaser.GameObjects.Graphics | null = null;

  constructor(scene: Phaser.Scene, tower: Tower) {
    this.scene = scene;
    this.tower = tower;
  }

  /** Initialize input listeners */
  enable(onSwipe: (result: SwipeResult) => void): void {
    this.onSwipe = onSwipe;
    this.isActive = true;

    this.swipeIndicator = this.scene.add.graphics();
    this.swipeIndicator.setDepth(100);

    this.scene.input.on("pointerdown", this.onPointerDown, this);
    this.scene.input.on("pointermove", this.onPointerMove, this);
    this.scene.input.on("pointerup", this.onPointerUp, this);
  }

  /** Disable input */
  disable(): void {
    this.isActive = false;
    this.scene.input.off("pointerdown", this.onPointerDown, this);
    this.scene.input.off("pointermove", this.onPointerMove, this);
    this.scene.input.off("pointerup", this.onPointerUp, this);
    if (this.swipeIndicator) {
      this.swipeIndicator.destroy();
      this.swipeIndicator = null;
    }
  }

  private onPointerDown(pointer: Phaser.Input.Pointer): void {
    if (!this.isActive) return;

    this.touchStartX = pointer.x;
    this.touchStartY = pointer.y;
    this.touchStartTime = pointer.time;

    // Determine target block based on Y position at touch start
    this.targetBlock = this.tower.getBlockAtY(pointer.y);
  }

  private onPointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.isActive || !this.targetBlock) return;

    // Draw swipe indicator
    if (this.swipeIndicator) {
      this.swipeIndicator.clear();
      const dx = pointer.x - this.touchStartX;
      if (Math.abs(dx) > StageConfig.minSwipePx) {
        this.swipeIndicator.lineStyle(3, 0xffff00, 0.6);
        this.swipeIndicator.lineBetween(
          this.touchStartX,
          this.touchStartY,
          pointer.x,
          this.touchStartY
        );
        // Arrow head
        const dir = dx > 0 ? -1 : 1;
        this.swipeIndicator.lineBetween(
          pointer.x,
          this.touchStartY,
          pointer.x + dir * 10,
          this.touchStartY - 8
        );
        this.swipeIndicator.lineBetween(
          pointer.x,
          this.touchStartY,
          pointer.x + dir * 10,
          this.touchStartY + 8
        );
      }
    }
  }

  private onPointerUp(pointer: Phaser.Input.Pointer): void {
    if (!this.isActive || !this.targetBlock) {
      this.clearIndicator();
      return;
    }

    const deltaX = pointer.x - this.touchStartX;
    const absDelta = Math.abs(deltaX);
    const elapsed = pointer.time - this.touchStartTime;

    this.clearIndicator();

    // Check minimum swipe distance
    if (absDelta < StageConfig.minSwipePx) {
      this.targetBlock = null;
      return;
    }

    // Clamp swipe distance
    const clampedDelta =
      Math.sign(deltaX) *
      clamp(absDelta, StageConfig.minSwipePx, StageConfig.maxSwipePx);

    const speed = elapsed > 0 ? absDelta / elapsed : 0;

    if (this.onSwipe && this.targetBlock && !this.targetBlock.isRemoved) {
      this.onSwipe({
        block: this.targetBlock,
        deltaX: clampedDelta,
        speed,
      });
    }

    this.targetBlock = null;
  }

  private clearIndicator(): void {
    if (this.swipeIndicator) {
      this.swipeIndicator.clear();
    }
  }

  destroy(): void {
    this.disable();
  }
}
